import { TargetView } from '@optools/services/operator-gateway/types/generated/graphql';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';

import { usePartnerData } from '../hooks/usePartnerData';
import { createTargetId } from '../utils/targets';
import { MappingView } from './mapping-view';
import { MappingViewButton } from './mapping-view-button';

export interface PartnersAndTargetsProps {
  activeTab: string | null;
  currentTargetId: number | undefined;
  loading: boolean;
  onSelectTarget: (targetId: string) => void;
  selectedTargets: Set<string>;
}

export function PartnersAndTargets({
  activeTab,
  currentTargetId,
  loading,
  onSelectTarget,
  selectedTargets,
}: PartnersAndTargetsProps) {
  const { channel: activeChannelParam, target: activeTargetParam } = useSearch({
    from: '/_auth/partner/mapping',
  });
  const navigate = useNavigate({ from: '/partner/mapping' });

  const [searchTerm, setSearchTerm] = useState('');

  const { partners } = usePartnerData();

  const handleClickTarget = (isSelected: boolean, target: TargetView) => {
    if (!activeChannelParam) {
      navigate({ search: (params) => ({ ...params, target: isSelected ? '' : target.name }) });
    } else {
      const id = createTargetId(target);
      onSelectTarget(id);
    }
  };

  return (
    <MappingView
      activeTab={activeTab}
      filtersTitle="Partners"
      items={partners}
      panelsTitle="Targets"
      renderFilter={({ name, targets }) => {
        const targetInPartner = targets.some((target) => target.id === currentTargetId);
        const targetsInFilter = targets.filter((target) => {
          const id = createTargetId(target);
          return selectedTargets.has(id);
        }).length;

        return (
          <>
            <span className="flex flex-1 justify-start break-all">{name}</span>

            {targetsInFilter ? (
              <span
                className="flex size-4 items-center justify-center rounded-full border border-current p-3 text-xs whitespace-nowrap tabular-nums"
                data-testid={`${name}-target-indicator`}>
                {targetsInFilter}
              </span>
            ) : null}

            {targetInPartner && (
              <span
                className="size-2 rounded-full bg-foreground transition-colors"
                data-testid={`${name}-partner-indicator`}
              />
            )}
          </>
        );
      }}
      renderPanel={({ targets }) => {
        const filteredTargets = targets.filter((target) => {
          const isActive = activeTargetParam === target.name;
          return new RegExp(searchTerm, 'gi').test(target.name) || isActive;
        });

        return filteredTargets.map((target) => {
          const id = createTargetId(target);
          const isSelected = activeTargetParam === target.name || selectedTargets.has(id);
          const isActive = activeTargetParam === target.name;
          return (
            <MappingViewButton
              key={target.name}
              isActive={isActive}
              loading={loading}
              pressed={isSelected}
              onClick={() => handleClickTarget(isSelected, target)}>
              {target.name}
            </MappingViewButton>
          );
        });
      }}
      onSearch={setSearchTerm}
    />
  );
}
